import React, { useRef, useState, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";

import styles from "./ContextMenu.module.scss";

export interface IContextMenuOption {
  label: string,
  onClick(e: any): void
}

interface IProps {
  label?: string,
  options: IContextMenuOption[],
  disablePortal?: boolean
}

const ContextMenu: React.FC<IProps> = (props) => {
  const [showMenu, setShowMenu] = useState<boolean>(false);

  const iconRef = useRef<HTMLDivElement>(null);
  const optionRef = useRef<HTMLUListElement>(null);

  const [top, setTop] = useState<number>(0);
  const [left, setLeft] = useState<number>(0);

  const toggleMenu = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setShowMenu((prev) => !prev);
  }, []);

  const handleScroll = () => {
    setShowMenu(false);
  };

  useEffect(() => {
    if (!showMenu || props.disablePortal) {
      return;
    }

    const rafId = window.requestAnimationFrame(() => {
      if (!iconRef.current || !optionRef.current) {
        return;
      }

      const iconRect = iconRef.current.getBoundingClientRect();
      const menuRect = optionRef.current.getBoundingClientRect();
      // Cap the effective menu height to match the CSS max-height so the
      // upward/downward decision is accurate when the list is scrollable.
      const maxMenuHeight = Math.min(menuRect.height, 360);
      const shouldOpenUpwards = iconRect.bottom + maxMenuHeight > window.innerHeight;

      const newTop = shouldOpenUpwards
        ? Math.max(8, iconRect.top - maxMenuHeight)
        : iconRect.bottom;
      setTop(newTop);
      setLeft(iconRect.right - menuRect.width);
    });

    return () => window.cancelAnimationFrame(rafId);
  }, [showMenu, props.options.length, props.disablePortal]);

  const menuStyle = useMemo(
    () => ({
      top: `${top}px`,
      left: `${left}px`,
    }),
    [top, left]
  );

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    if (!showMenu) return;
    const close = (event: MouseEvent) => {
      const target = event.target as Node;
      if (iconRef.current?.contains(target) || optionRef.current?.contains(target)) {
        return;
      }
      setShowMenu(false);
    };
    window.addEventListener("mousedown", close);
    return () => window.removeEventListener("mousedown", close);
  }, [showMenu]);

  const optionsMenu = (
    <ul
      ref={optionRef}
      style={props.disablePortal ? undefined : menuStyle}
      className={`${styles.ContextMenuOptions} ${props.disablePortal ? styles.Inline : ""}`}
      onMouseLeave={() => { setShowMenu(false); }}
    >
      {props.options.map((option: IContextMenuOption, index: number) => {
        return (
          <li key={index} className={styles.option} onClick={(e) => { option.onClick(e); setShowMenu(false); }}>
            {option.label}
          </li>
        );
      })}
    </ul>
  );

  return (
    <div className={styles.ContextMenu}>
      <div
        ref={iconRef}
        onClick={toggleMenu}
        className={styles.innerLabel}
      >
        {props.label !== undefined && <div className={styles.labelWrap}>{props.label}</div>}
        <div className={styles.icon}></div>
      </div>
      {showMenu && (props.disablePortal ? optionsMenu : createPortal(optionsMenu, document.body))}
    </div>
  );
};

export default ContextMenu;